import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['content', 'panel', 'icon', 'spacing', 'toggle'];
  static values = {
    expanded: { type: Boolean, default: true },
    persistKey: { type: String, default: '' },
  };

  connect() {
    if (this.persistKeyValue) {
      try {
        const stored = sessionStorage.getItem(this._storageKey());
        if (stored === 'true') this.expandedValue = true;
        else if (stored === 'false') this.expandedValue = false;
      } catch (_e) {
        // noop
      }
    }

    if (this.expandedValue) this._applyExpandedInitial();
    else this._applyCollapsedInitial();

    this._syncDatasetAndDispatch();

    this.element.dispatchEvent(
      new CustomEvent('collapsable-section:connected', {
        bubbles: true,
      })
    );

    this._onChildOpen = () => this._elevate();
    this._onChildClose = () => this._lower();
    this.element.addEventListener('select:open', this._onChildOpen);
    this.element.addEventListener('select:close', this._onChildClose);

    this._onExpandEvt = () => this.expand();
    this._onCollapseEvt = () => this.collapse();
    this.element.addEventListener('collapsable-section:expand', this._onExpandEvt);
    this.element.addEventListener('collapsable-section:collapse', this._onCollapseEvt);
  }

  disconnect() {
    this.element.removeEventListener('select:open', this._onChildOpen);
    this.element.removeEventListener('select:close', this._onChildClose);
    this.element.removeEventListener('collapsable-section:expand', this._onExpandEvt);
    this.element.removeEventListener('collapsable-section:collapse', this._onCollapseEvt);

    this.element.dispatchEvent(
      new CustomEvent('collapsable-section:disconnected', {
        bubbles: true,
      })
    );
  }

  toggle() {
    this.expandedValue ? this.collapse() : this.expand();
  }

  onKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggle();
    }
  }

  expand() {
    if (this.expandedValue) return;

    this.contentTarget.classList.remove('grid-rows-[0fr]');
    this.contentTarget.classList.add('grid-rows-[1fr]');

    this.panelTarget.classList.remove('opacity-0');
    this.panelTarget.classList.add('opacity-100');

    if (this.hasSpacingTarget) {
      this.spacingTarget.classList.remove('h-0');
      this.spacingTarget.classList.add('h-72');
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.add('rotate-180');
    }

    this.expandedValue = true;
    this._persistState();
  }

  collapse() {
    if (!this.expandedValue) return;

    this.contentTarget.classList.remove('grid-rows-[1fr]');
    this.contentTarget.classList.add('grid-rows-[0fr]');

    this.panelTarget.classList.remove('opacity-100');
    this.panelTarget.classList.add('opacity-0');

    if (this.hasSpacingTarget) {
      this.spacingTarget.classList.remove('h-72');
      this.spacingTarget.classList.add('h-0');
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.remove('rotate-180');
    }

    this.expandedValue = false;
    this._persistState();
  }


  expandedValueChanged() {
    this._syncDatasetAndDispatch();
  }

  _applyExpandedInitial() {
    this.contentTarget.classList.add('grid-rows-[1fr]');
    this.contentTarget.classList.remove('grid-rows-[0fr]');
    this.panelTarget.classList.add('opacity-100');
    this.panelTarget.classList.remove('opacity-0');
    if (this.hasSpacingTarget) {
      this.spacingTarget.classList.add('h-72');
      this.spacingTarget.classList.remove('h-0');
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.add('rotate-180');
    }
  }

  _applyCollapsedInitial() {
    this.contentTarget.classList.add('grid-rows-[0fr]');
    this.contentTarget.classList.remove('grid-rows-[1fr]');
    this.panelTarget.classList.add('opacity-0');
    this.panelTarget.classList.remove('opacity-100');
    if (this.hasSpacingTarget) {
      this.spacingTarget.classList.add('h-0');
      this.spacingTarget.classList.remove('h-72');
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.remove('rotate-180');
    }
  }

  _syncDatasetAndDispatch() {
    this.element.dataset.collapsableSectionExpandedValue = String(this.expandedValue);
    if (this.hasToggleTarget) {
      this.toggleTargets.forEach((t) => t.setAttribute('aria-expanded', String(this.expandedValue)));
    }
    this.element.dispatchEvent(
      new CustomEvent('collapsable-section:state', {
        detail: { expanded: this.expandedValue },
        bubbles: true,
      })
    );
  }

  _elevate() { this.element.classList.add('z-[110]'); }
  _lower()   { this.element.classList.remove('z-[110]'); }

  _storageKey() {
    return `collapsable-section:${this.persistKeyValue}`;
  }

  _persistState() {
    if (!this.persistKeyValue) return;
    try {
      sessionStorage.setItem(this._storageKey(), String(this.expandedValue));
    } catch (_e) {
      // noop
    }
  }
}
